import { useEffect, useRef, useState } from "react";
import { teamApi } from "../../services/teamApi";
import { accountApi } from "../../services/accountApi";
import { useToast } from "../../context/ToastContext";
import { useTeamRole } from "../../hooks/useTeamRole";
import { ListBound } from "../../components/common/ListBound";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { Spinner } from "../../components/common/Spinner";
import { EmptyState } from "../../components/common/EmptyState";
import { RoleManagementSection } from "../../components/team/RoleManagementSection";
import { Field } from "../../components/common/Field";
import { HivePicker } from "../../components/common/HivePicker";
import { ActivityFilters } from "../../components/team/ActivityFilters";

// Matches the "word currently being typed" when it starts with @, so we can
// offer a pick-list of team member emails as the user types (server-side
// matching in api/functions/team.py's _notify_mentions is a plain
// `@email@domain` regex + exact-email lookup against the team, so this just
// helps the user type a valid, matchable mention -- it doesn't change what
// counts as a mention).
const MENTION_PATTERN = /@([^\s@]*)$/;

export function TeamDashboardPage() {
  const { notify } = useToast();
  const { canCreate, canManage, teamEmails } = useTeamRole();
  const [feed, setFeed] = useState(null);
  // How far the feed goes (#372). Only set when the server had to leave
  // rows behind, so the notice never appears on a short feed.
  const [feedCappedAt, setFeedCappedAt] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [commentForm, setCommentForm] = useState({ hive_id: "", comment: "" });
  const [viewHiveId, setViewHiveId] = useState("");
  const [comments, setComments] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [mentionMatches, setMentionMatches] = useState([]);
  const commentRef = useRef(null);
  // Blur closes the @mention list on a short delay so a click on a suggestion
  // still lands. Held so navigating away mid-delay does not set state after.
  const mentionBlurTimerRef = useRef(null);

  // null = still loading, [] = genuinely empty, feedError = we could not
  // find out. Those are three different things and the UI used to render
  // the third as the first -- a spinner that never resolves, which tells
  // the user nothing at all.
  const [feedError, setFeedError] = useState(null);
  // The current filter (#314). Held here rather than inside the controls
  // so a reload after posting a comment keeps the window the user chose.
  const [feedFilter, setFeedFilter] = useState({});
  // People to offer in the "who" list: approved members of this account.
  const [people, setPeople] = useState([]);

  async function load() {
    // Each call settles on its own. Previously activityFeed() had no catch
    // inside a Promise.all, so ONE failure rejected the whole thing, both
    // setState calls were skipped, and every panel span forever.
    const [feedRes, notifRes] = await Promise.allSettled([
      teamApi.activityFeed(feedFilter),
      teamApi.listNotifications(),
    ]);

    if (feedRes.status === "fulfilled") {
      setFeed(feedRes.value.activity || []);
      setFeedCappedAt(feedRes.value.capped_at ?? null);
      setFeedError(null);
    } else {
      // 403 here is expected, not broken: the feed is gated on
      // ("TeamCollaboration", "team_activity_feed"). Saying so beats
      // implying a fault the user cannot fix.
      const status = feedRes.reason?.response?.status;
      setFeed([]);
      setFeedError(
        status === 403
          ? "Team activity isn't included on your current plan."
          : "Couldn't load team activity.",
      );
    }

    setNotifications(notifRes.status === "fulfilled" ? notifRes.value.notifications || [] : []);
  }
  useEffect(() => { load(); }, []);

  // Refetch when the window or the person changes. Separate from load()
  // so changing a filter does not also re-pull notifications.
  useEffect(() => {
    let cancelled = false;
    teamApi
      .activityFeed(feedFilter)
      .then((r) => !cancelled && (setFeed(r.activity || []), setFeedError(null)))
      // Silent here: load() already reported whether the feed is
      // reachable at all, and a second message for the same cause reads
      // as two problems.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [feedFilter]);

  // Who can appear in the "who" list. Best-effort: not knowing the names
  // must not stop the feed rendering, and the filter simply hides.
  useEffect(() => {
    let cancelled = false;
    accountApi
      .listShares()
      .then((r) => {
        if (cancelled) return;
        const approved = (r.sent || []).filter((sh) => sh.status === "Approved");
        setPeople(approved.map((sh) => ({ user_id: sh.shared_user_id, label: sh.shared_email })));
      })
      .catch(() => !cancelled && setPeople([]));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => clearTimeout(mentionBlurTimerRef.current), []);

  async function loadComments(hiveId) {
    if (!hiveId) return notify("Enter a hive ID to view its comments", "warning");
    const r = await teamApi.listComments(Number(hiveId));
    setComments(r.comments);
    setViewHiveId(hiveId);
  }

  async function submitComment() {
    if (!commentForm.hive_id || !commentForm.comment.trim()) return notify("Hive ID and comment are required", "warning");
    try {
      await teamApi.addComment({ hive_id: Number(commentForm.hive_id), comment: commentForm.comment });
      notify("Comment added", "success");
      const hiveId = commentForm.hive_id;
      setCommentForm({ hive_id: "", comment: "" });
      setMentionMatches([]);
      load();
      if (String(viewHiveId) === String(hiveId)) loadComments(hiveId);
    } catch (err) {
      notify(err.apiMessage || "Could not add comment", "danger");
    }
  }

  function onCommentChange(e) {
    const value = e.target.value;
    setCommentForm((f) => ({ ...f, comment: value }));

    const upToCursor = value.slice(0, e.target.selectionStart ?? value.length);
    const match = upToCursor.match(MENTION_PATTERN);
    if (!match) {
      setMentionMatches([]);
      return;
    }
    const partial = match[1].toLowerCase();
    setMentionMatches(teamEmails.filter((email) => email.toLowerCase().includes(partial)).slice(0, 6));
  }

  function selectMention(email) {
    const textarea = commentRef.current;
    const value = commentForm.comment;
    const cursor = textarea ? textarea.selectionStart ?? value.length : value.length;
    const upToCursor = value.slice(0, cursor);
    const match = upToCursor.match(MENTION_PATTERN);
    const start = match ? cursor - match[1].length - 1 : cursor;
    const nextValue = `${value.slice(0, start)}@${email} ${value.slice(cursor)}`;
    setCommentForm((f) => ({ ...f, comment: nextValue }));
    setMentionMatches([]);
    requestAnimationFrame(() => textarea?.focus());
  }

  function startEdit(c) {
    setEditingId(c.comment_id);
    setEditText(c.comment);
  }

  async function saveEdit(commentId) {
    try {
      await teamApi.updateComment(commentId, editText);
      setEditingId(null);
      loadComments(viewHiveId);
    } catch (err) {
      notify(err.apiMessage || "Could not update comment", "danger");
    }
  }

  async function removeComment(commentId) {
    try {
      await teamApi.deleteComment(commentId);
      loadComments(viewHiveId);
    } catch (err) {
      notify(err.apiMessage || "Could not delete comment", "danger");
    }
  }

  async function markRead(notificationId) {
    await teamApi.markNotificationRead(notificationId);
    load();
  }

  const unreadCount = (notifications || []).filter((n) => !n.is_read).length;

  return (
    <div>
      <h1>Team Dashboard</h1>

      <Card
        title="Notifications"
        actions={unreadCount > 0 ? <Badge tone="danger">{unreadCount}</Badge> : <Badge tone="success">0</Badge>}
        className="section-spacing"
      >
        {!notifications ? <Spinner /> : notifications.length === 0 ? (
          <EmptyState title="No notifications yet" message="Task assignments, @mentions, and handoffs will show up here." />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {notifications.slice(0, 20).map((n) => (
              <li
                key={n.notification_id}
                style={{
                  padding: "var(--space-2) 0",
                  borderBottom: "1px solid var(--color-border)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  opacity: n.is_read ? 0.6 : 1,
                }}
              >
                <div>
                  <strong>{n.title}</strong>
                  <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>{n.body}</div>
                </div>
                {!n.is_read && <Button size="sm" variant="ghost" onClick={() => markRead(n.notification_id)}>Mark read</Button>}
              </li>
            ))}
          </ul>
        )}
        {/* AT THE BOTTOM, where the question is actually asked (#372).
            A banner above the list answers something nobody has wondered
            yet; this is read at the moment somebody reaches the end. The
            feed HAS filters, so the notice can point at the way past the
            cap rather than being a dead end. */}
        <ListBound cappedAt={feedCappedAt} noun="entries" filterable />
      </Card>

      {/* ABOVE the feed and the comment list (#371). It is an ACTION
          and both of those are history, which is the order
          UI_ACTIONS.md asks for.

          It sat below BOTH, and the feed is TOP 200 server-side -- so
          at full length the box was below two hundred rows plus every
          comment. Not 'pushed down': unreachable in practice.

          Same position as mobile now, and the same position 'Assign a
          Task' holds on the Tasks tab. Siblings agreeing is worth more
          than either arrangement on its own. */}
      <Card title="Add Comment to a Hive" className="section-spacing">
        {canCreate ? (
          <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: 400 }}>
            <HivePicker label="Hive" value={commentForm.hive_id} onChange={(v) => setCommentForm((f) => ({ ...f, hive_id: v }))} />
            <div style={{ position: "relative" }}>
              <Field label="Comment" hint="Type @ to mention a team member">
                {(id, aria) => (
              <textarea
                id={id}
                {...aria}
                ref={commentRef}
                rows={3}
                value={commentForm.comment}
                onChange={onCommentChange}
                onBlur={() => {
                  clearTimeout(mentionBlurTimerRef.current);
                  mentionBlurTimerRef.current = setTimeout(() => setMentionMatches([]), 150);
                }}
                style={{ width: "100%" }}
              />
                )}
              </Field>
              {mentionMatches.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: "100%",
                    marginTop: "var(--space-1)",
                    background: "var(--color-bg-elevated)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-md)",
                    zIndex: 10,
                    overflow: "hidden",
                  }}
                >
                  {mentionMatches.map((email) => (
                    <button
                      key={email}
                      type="button"
                      onClick={() => selectMention(email)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "var(--space-2) var(--space-3)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--color-text)",
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      @{email}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={submitComment}>Post Comment</Button>
          </div>
        ) : (
          <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            Your Viewer role is read-only -- posting comments is disabled.
          </p>
        )}
      </Card>
      <Card title="Activity Feed" className="section-spacing">
        {/* Who, and between when (#314). The everyday question -- what did
            my helper do today -- and the uncomfortable one both use these,
            so the app needs no incident mode. */}
        <ActivityFilters people={people} onChange={setFeedFilter} />

        {feedError ? (
          <EmptyState title={feedError} />
        ) : !feed ? <Spinner /> : feed.length === 0 ? (
          <EmptyState
            title={Object.keys(feedFilter).some((k) => feedFilter[k])
              ? "Nothing in that window"
              : "No team activity yet"}
            description={Object.keys(feedFilter).some((k) => feedFilter[k])
              ? "Try a wider date range, or everyone."
              : undefined}
          />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {feed.map((a) => (
              <li key={a.feed_id} style={{ padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
                {/* The name comes from the row's user_id now, not from
                    the action text (#286). Older rows still carry an
                    email inside their prose -- an activity log is a
                    record, not a display cache. */}
                <strong>
                  {a.performed_by ? `${a.performed_by} ` : ""}
                  {a.action}
                </strong>
                <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>{new Date(a.timestamp).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title="Hive Comments" className="section-spacing">
        <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: 500 }}>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <HivePicker label="Hive" value={viewHiveId} onChange={setViewHiveId} />
            <Button variant="secondary" onClick={() => loadComments(viewHiveId)}>View Comments</Button>
          </div>

          {comments && (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {comments.length === 0 ? (
                <EmptyState title="No comments on this hive yet" />
              ) : (
                comments.map((c) => (
                  <li key={c.comment_id} style={{ padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
                    {editingId === c.comment_id ? (
                      <div style={{ display: "grid", gap: "var(--space-2)" }}>
                        <textarea id="team-dashboard-page-edit-text" name="edit-text" rows={2} value={editText} onChange={(e) => setEditText(e.target.value)} />
                        <div style={{ display: "flex", gap: "var(--space-2)" }}>
                          <Button size="sm" onClick={() => saveEdit(c.comment_id)}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>{c.comment}{c.edited && <span style={{ color: "var(--color-text-muted)" }}> (edited)</span>}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>{new Date(c.timestamp).toLocaleString()}</span>
                          <div style={{ display: "flex", gap: "var(--space-2)" }}>
                            <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>Edit</Button>
                            {canManage && <Button size="sm" variant="danger" onClick={() => removeComment(c.comment_id)}>Delete</Button>}
                          </div>
                        </div>
                      </>
                    )}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </Card>


      <h2>Role Management</h2>
      <RoleManagementSection />
    </div>
  );
}
